import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { authAPI } from './api';
import { clearSignupRequestId, signupRequestId } from './setupRequest';
import { clearAllCache } from './cache';
import { resetReviewSession } from './reviewFeedCache';
import { clearAll as clearContactStore } from './contactsDb';

const ACTIVE_USER_KEY = 'pik_active_user_id';

let publishedReviewUser;
function publishActiveUser(id) {
  const next = id ? String(id) : '';
  if (publishedReviewUser !== undefined && publishedReviewUser !== next) resetReviewSession();
  publishedReviewUser = next;
  try {
    if (id) localStorage.setItem(ACTIVE_USER_KEY, String(id));
    else localStorage.removeItem(ACTIVE_USER_KEY);
  } catch { /* private mode */ }
}

const AuthContext = createContext(null);

/**
 * AuthProvider — cookie-based JWT (httpOnly).
 * The token is stored in a server-set httpOnly cookie and sent automatically
 * via `withCredentials: true`. We no longer store the JWT in localStorage.
 *
 * `token` is kept in React state only as a truthy presence-marker for
 * components that check "is the user logged in" — its actual value is not
 * what authenticates the API call (the cookie does).
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const userRef = useRef(null);
  userRef.current = user;

  const fetchUser = useCallback(async () => {
    // Session 126t (fix #7) — switched the bootstrap probe from
    // /auth/me (401 when not authed) to /auth/session (200 in both
    // cases). The network panel was showing 8x "401 Failed to load
    // resource" on every /login load because the probe + visibility
    // handler + retry all 401'd. The soft endpoint returns
    // `{ authenticated, user }` so we still distinguish the two
    // states cleanly.
    //
    // Retry-once-with-tiny-delay on the boot probe. Belt-and-suspenders
    // for iOS Safari, which occasionally drops the auth cookie on the
    // first XHR after a cold tab open / app-switcher resume. A second
    // attempt 200ms later almost always succeeds when the session is
    // genuinely alive; we only fall through to logged-out state if
    // BOTH attempts fail.
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const { data } = await authAPI.session();
        if (data?.authenticated && data?.user) {
          setUser(data.user);
          setToken('cookie');
          publishActiveUser(data.user.id);
        } else {
          setUser(null);
          setToken(null);
          publishActiveUser(null);
        }
        setLoading(false);
        return;
      } catch {
        if (attempt === 0) {
          await new Promise((r) => setTimeout(r, 200));
          continue;
        }
        setUser(null);
        setToken(null);
        publishActiveUser(null);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  // Another tab signed in or out. Drop this tab's account immediately and
  // show the neutral session probe instead of the previous person's Home.
  useEffect(() => {
    const onStorage = (event) => {
      if (event.key !== ACTIVE_USER_KEY) return;
      const next = event.newValue || null;
      const current = userRef.current?.id ? String(userRef.current.id) : null;
      if (next === current) return;
      setLoading(true);
      setUser(null);
      setToken(null);
      fetchUser();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchUser]);

  // Slide-the-session-on-visibility — when the user returns to the tab
  // after backgrounding (or the browser wakes from a sleep state on iPhone
  // Chrome / Safari), re-probe `/auth/me`. The backend re-issues the auth
  // cookie + JWT with a fresh exp on every `/auth/me` hit, so this single
  // call both (a) confirms the session is still alive before the user
  // starts tapping around in stale cached UI, and (b) extends the cookie
  // lifetime by another 30 days (extended sessions) — no separate refresh
  // round-trip required.
  //
  // Debounced to once per minute so quickly hopping in and out of the tab
  // doesn't hammer the endpoint.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let lastProbe = 0;
    const isUserTyping = () => {
      const el = document.activeElement;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable === true;
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      // Don't probe while the user is mid-keystroke — on mobile Safari the
      // keyboard appearing/dismissing can fire focus events that previously
      // caused a network round-trip and cascade re-render mid-edit. The
      // session is rechecked the next time the user blurs the input.
      if (isUserTyping()) return;
      const now = Date.now();
      if (now - lastProbe < 60_000) return;
      lastProbe = now;
      // Fire-and-forget — fetchUser handles success/failure and updates
      // React state. Failures bubble through the 401 interceptor's session
      // re-probe path so the user only gets bounced to /login when the
      // session is genuinely gone.
      fetchUser();
    };
    document.addEventListener('visibilitychange', onVisible);
    // Also probe on hard window focus — iOS sometimes fires `focus` without
    // `visibilitychange` when returning from another app via the App
    // Switcher (a common idle-return pattern that was previously dropping
    // the user to /login).
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [fetchUser]);

  const login = async (email, password, opts = {}) => {
    const { remember_me = false } = opts;
    const { data } = await authAPI.login({ email, password, remember_me });
    // MFA challenge — caller must finish via `completeMfa()`.
    if (data?.mfa_required) {
      return { mfa_required: true, mfa_token: data.mfa_token, methods: data.methods || [] };
    }
    // Bulletproof: confirm the auth cookie is honoured by the browser
    // BEFORE we hand control back. iOS Safari (and ITP-enabled WebKit
    // variants) sometimes don't attach a freshly-Set-Cookie to the
    // immediate next XHR. We retry once with a tiny delay; if it still
    // fails we surface the failure rather than letting the caller
    // navigate into a half-authenticated state.
    let meData = null;
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const meRes = await authAPI.me();
        meData = meRes.data;
        break;
      } catch (err) {
        if (attempt === 0) {
          // Tiny wait — gives Safari time to flush the cookie store.
          await new Promise((r) => setTimeout(r, 250));
          continue;
        }
        // Both attempts failed → genuine session-setup failure.
        throw err;
      }
    }
    setToken('cookie');
    setUser(meData || data.user);
    publishActiveUser((meData || data.user)?.id);
    try {
      localStorage.removeItem('pik_token');
      localStorage.removeItem('pik_user');
    } catch (e) { console.warn('localStorage cleanup failed', e); }
    return meData || data.user;
  };

  const completeMfa = async ({ mfa_token, code }) => {
    const { data } = await authAPI.mfaVerify({ mfa_token, code });
    setToken('cookie');
    setUser(data.user);
    publishActiveUser(data.user?.id);
    return data;
  };

  const register = async (payload) => {
    const body = {
      ...payload,
      signup_request_id: payload?.signup_request_id || signupRequestId(),
    };
    const { data } = await authAPI.register(body);
    // A recovered sign-up on an MFA account is the same challenge as login.
    // No cookie and no user until that challenge finishes.
    if (data?.mfa_required) {
      return { mfa_required: true, mfa_token: data.mfa_token, methods: data.methods || [] };
    }
    clearSignupRequestId();
    setToken('cookie');
    setUser(data.user);
    publishActiveUser(data.user?.id);
    try {
      localStorage.removeItem('pik_token');
      localStorage.removeItem('pik_user');
    } catch (e) { console.warn('localStorage cleanup failed', e); }
    return data.user;
  };

  const logout = async () => {
    try { await authAPI.logout(); } catch (e) { console.warn('logout call failed', e); }
    setToken(null);
    setUser(null);
    // Clear cached API responses and the on-device contact book so the
    // next account on this browser cannot paint the previous book.
    clearAllCache();
    publishActiveUser(null);
    try { await clearContactStore(); } catch (e) { console.warn('contact store cleanup failed', e); }
    try {
      localStorage.removeItem('pik_token');
      localStorage.removeItem('pik_user');
    } catch (e) { console.warn('localStorage cleanup failed', e); }
  };

  const refreshUser = async () => {
    try {
      const { data } = await authAPI.me();
      setUser(data);
    } catch { /* ignore */ }
  };

  // Memoise the context value so consumers don't re-render on every parent
  // render — the object identity changes when any of these props change.
  const value = useMemo(
    () => ({ user, token, loading, login, completeMfa, register, logout, refreshUser }),
    [user, token, loading]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
